import { useState } from "react";
import type { UploadFile } from "antd/es/upload/interface";
import { message } from "antd";
import moment from "moment";

import {
  TradeConfirmationRow,
  DetailedWithdrawRequestRow,
  PurchaseHistoryRow,
  BankStatementRow,
  SoldStockRow,
  BondMetadataRow,
  ChargeRow,
  BondDistributionFile,
  UserBankInfo,
  BalanceHistoryEntry,
  MissingSheet8UsdEntry,
} from "./types";
import {
  parseDetailedWithdrawRequest,
  parsePurchaseHistory,
  parseBankStatement,
  parseTradeConfirmation,
  parseSoldStock,
  parseBondMetadata,
  parseCharge,
  parseGenericExcel,
} from "./parsers";
import {
  fetchBondMetadataFromSheets as apiFetchBondMetadata,
  fetchPortfolioAllocations,
} from "./api";
import {
  fetchCustomerDataByEmail as apiFetchCustomerData,
  lookupUserBankInfoByEmail as lookupUserBankInfo,
} from "./refundApi";
import {
  buildBondMetaMap,
  buildTCPriceMap,
  processWithdrawData,
  processCashBalanceRows,
  processBalanceHistoryAndBondRetake,
  prepareBondRetakeWithTailbar,
} from "./processing";
import {
  downloadAccumulatedPurchaseData,
  downloadAccumulatedSoldStock,
  downloadAccumulatedCharge,
  generate8SheetExcel,
} from "./generators";

// Generic factory for foundation/addSheet handler pairs
const createAccumulativeHandlers = <T>(
  parser: (file: File) => Promise<{ name: string; data: T[] }[]>,
  setter: React.Dispatch<React.SetStateAction<{ name: string; data: T[] }[]>>,
  getCurrentSheets: () => { name: string; data: T[] }[],
) => ({
  handleFoundation: async (file: File) => {
    try {
      const parsed = await parser(file);
      setter(parsed);
      message.success(`${parsed.length} sheet суурь файлаас ачаалагдлаа`);
    } catch (error) {
      console.error(error);
      message.error("Суурь файл уншихад алдаа гарлаа");
    }
  },
  handleAddSheet: async (file: File) => {
    try {
      const parsed = await parser(file);
      const existingNames = new Set(getCurrentSheets().map(s => s.name));
      const newSheets = parsed.map(sheet => {
        let name = sheet.name;
        let counter = 1;
        while (existingNames.has(name)) {
          name = `${sheet.name}_${counter++}`;
        }
        existingNames.add(name);
        return { ...sheet, name };
      });
      setter(prev => [...prev, ...newSheets]);
      message.success(`${parsed.length} sheet нэмэгдлээ`);
    } catch (error) {
      console.error(error);
      message.error("Файл уншихад алдаа гарлаа");
    }
  },
});

export const usePart2 = (
  combinedTC: TradeConfirmationRow[],
  bondDistributionData: BondDistributionFile[] = [],
) => {
  // File states
  const [detailedWithdrawRequestFiles, setDetailedWithdrawRequestFiles] = useState<UploadFile[]>([]);
  const [detailedWithdrawData, setDetailedWithdrawData] = useState<DetailedWithdrawRequestRow[]>([]);
  const [purchaseHistoryFiles, setPurchaseHistoryFiles] = useState<UploadFile[]>([]);
  const [accumulatedPurchaseData, setAccumulatedPurchaseData] = useState<PurchaseHistoryRow[]>([]);
  const [bankStatementFiles, setBankStatementFiles] = useState<UploadFile[]>([]);
  const [bankStatementData, setBankStatementData] = useState<BankStatementRow[]>([]);
  const [custodyInstructionFiles, setCustodyInstructionFiles] = useState<UploadFile[]>([]);
  const [custodyInstructionData, setCustodyInstructionData] = useState<TradeConfirmationRow[]>([]);
  const [accumulatedSoldStockSheets, setAccumulatedSoldStockSheets] = useState<{ name: string; data: SoldStockRow[] }[]>([]);
  const [bondMetadataFiles, setBondMetadataFiles] = useState<UploadFile[]>([]);
  const [savedBondMetadata, setSavedBondMetadata] = useState<BondMetadataRow[] | null>(null);
  const [accumulatedChargeSheets, setAccumulatedChargeSheets] = useState<{ name: string; data: ChargeRow[] }[]>([]);
  const [customerBondFiles, setCustomerBondFiles] = useState<Record<string, {
    purchaseData: Record<string, unknown>[];
    txHistoryData: Record<string, unknown>[];
  }>>({});

  // Processing states
  const [part2Loading, setPart2Loading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [userBankInfoMap, setUserBankInfoMap] = useState<Record<string, UserBankInfo>>({});
  const [bondMetaLoading, setBondMetaLoading] = useState(false);
  const [customerDataLoading, setCustomerDataLoading] = useState(false);
  const [userBalanceHistoryMap, setUserBalanceHistoryMap] = useState<Record<string, BalanceHistoryEntry[]>>({});

  // Configuration
  const [interestEndDate, setInterestEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [usdRate, setUsdRate] = useState<number>(3536);
  const [excludedUsers, setExcludedUsers] = useState<Set<string>>(new Set());
  const [missingSheet8UsdEntries, setMissingSheet8UsdEntries] = useState<MissingSheet8UsdEntry[]>([]);
  const [manualSheet8UsdRates, setManualSheet8UsdRates] = useState<Record<string, number>>({});

  // File upload handlers
  const handleDetailedWithdrawUpload = async (files: UploadFile[]) => {
    setDetailedWithdrawRequestFiles(files);
    if (files.length === 0) return;

    try {
      const allData: DetailedWithdrawRequestRow[] = [];
      for (const file of files) {
        if (!file.originFileObj) continue;
        const parsed = await parseDetailedWithdrawRequest(file.originFileObj);
        allData.push(...parsed);
      }

      const seen = new Set<string>();
      const uniqueData = allData.filter(row => {
        const key = `${row.email.toLowerCase().trim()}|${row.assetName}|${row.purchaseDate}|${row.quantity}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const duplicateCount = allData.length - uniqueData.length;
      setDetailedWithdrawData(uniqueData);

      const uniqueCustomersByRegNumber = new Set(uniqueData.map(row => row.regNumber.trim())).size;
      message.success(`${uniqueCustomersByRegNumber} харилцагчийн мэдээлэл уншигдлаа (${uniqueData.length} мөр)${duplicateCount > 0 ? ` (${duplicateCount} давхардал хассан)` : ''}`);
    } catch (error) {
      console.error(error);
      message.error("Файл уншихад алдаа гарлаа");
    }
  };

  const handlePurchaseHistoryUpload = async (files: UploadFile[]) => {
    setPurchaseHistoryFiles(files);
    if (files.length === 0) {
      setAccumulatedPurchaseData([]);
      return;
    }

    try {
      const newFile = files[files.length - 1];
      if (!newFile.originFileObj) return;
      const parsed = await parsePurchaseHistory(newFile.originFileObj);
      // Purchase history should reflect the latest uploaded source file, not
      // accumulate old uploads across retries.
      setAccumulatedPurchaseData(parsed);
      message.success(`${parsed.length} мөр шинэчлэгдлээ`);
    } catch (error) {
      console.error(error);
      message.error("Файл уншихад алдаа гарлаа");
    }
  };

  const handleBankStatementUpload = async (files: UploadFile[]) => {
    setBankStatementFiles(files);
    if (files.length === 0) return;

    try {
      const file = files[files.length - 1];
      if (!file.originFileObj) return;
      const parsed = await parseBankStatement(file.originFileObj);
      setBankStatementData(parsed);
      message.success(`${parsed.length} гүйлгээ уншигдлаа`);
    } catch (error) {
      console.error(error);
      message.error("Файл уншихад алдаа гарлаа");
    }
  };

  const handleCustodyInstructionUpload = async (files: UploadFile[]) => {
    setCustodyInstructionFiles(files.slice(-1));
    setMissingSheet8UsdEntries([]);
    setManualSheet8UsdRates({});
    if (files.length === 0) {
      setCustodyInstructionData([]);
      return;
    }

    try {
      const file = files[files.length - 1];
      if (!file.originFileObj) return;
      const parsed = await parseTradeConfirmation(file.originFileObj);
      setCustodyInstructionData(parsed);
      message.success(`${parsed.length} custody мөр уншигдлаа`);
    } catch (error) {
      console.error(error);
      message.error("Custody file уншихад алдаа гарлаа");
    }
  };

  const handleBondMetadataUpload = async (files: UploadFile[]) => {
    setBondMetadataFiles(files.slice(-1));
    if (files.length === 0) return;

    try {
      const file = files[files.length - 1];
      if (!file.originFileObj) return;
      const parsed = await parseBondMetadata(file.originFileObj);
      setSavedBondMetadata(parsed);
      message.success(`${parsed.length} бондын мэдээлэл уншигдлаа`);
    } catch (error) {
      console.error(error);
      message.error("Файл уншихад алдаа гарлаа");
    }
  };

  // Accumulative handlers for Sold Stock
  const soldStockHandlers = createAccumulativeHandlers(
    parseSoldStock,
    setAccumulatedSoldStockSheets,
    () => accumulatedSoldStockSheets,
  );

  // Accumulative handlers for Charge
  const chargeHandlers = createAccumulativeHandlers(
    parseCharge,
    setAccumulatedChargeSheets,
    () => accumulatedChargeSheets,
  );

  // Per-customer file handlers
  const handleCustomerPurchaseFile = async (regNumber: string, file: File) => {
    try {
      const data = await parseGenericExcel(file);
      setCustomerBondFiles(prev => ({
        ...prev,
        [regNumber]: { ...prev[regNumber], purchaseData: data, txHistoryData: prev[regNumber]?.txHistoryData || [] },
      }));
      message.success(`${regNumber}: Худалдан авалт ${data.length} мөр`);
    } catch (error) {
      console.error(error);
      message.error(`${regNumber}: Файл уншихад алдаа`);
    }
  };

  const handleCustomerTxHistoryFile = async (regNumber: string, file: File) => {
    try {
      const data = await parseGenericExcel(file);
      setCustomerBondFiles(prev => ({
        ...prev,
        [regNumber]: { ...prev[regNumber], purchaseData: prev[regNumber]?.purchaseData || [], txHistoryData: data },
      }));
      message.success(`${regNumber}: Гүйлгээний түүх ${data.length} мөр`);
    } catch (error) {
      console.error(error);
      message.error(`${regNumber}: Файл уншихад алдаа`);
    }
  };

  // Fetch bond metadata from Google Sheets (wrapper with state)
  const fetchBondMetadataFromSheets = async () => {
    setBondMetaLoading(true);
    try {
      const result = await apiFetchBondMetadata();
      setSavedBondMetadata(result);
      message.success(`${result.length} бондын мэдээлэл Google Sheets-ээс татагдлаа`);
    } catch (error) {
      console.error("Failed to fetch bond metadata:", error);
      message.error("Бондын мэдээлэл татахад алдаа гарлаа");
    } finally {
      setBondMetaLoading(false);
    }
  };

  // Fetch customer data from API (wrapper with state)
  const fetchCustomerDataFromAPI = async () => {
    if (detailedWithdrawData.length === 0) {
      message.error("Эхлээд буцаалтын хүсэлт файл оруулна уу");
      return;
    }

    setCustomerDataLoading(true);
    try {
      const { files, successCount, totalCount, skipped } = await apiFetchCustomerData(detailedWithdrawData);
      setCustomerBondFiles(prev => ({ ...prev, ...files }));
      if (skipped.length > 0) {
        console.warn("Skipped users:", skipped);
        message.warning(`${successCount}/${totalCount} татагдлаа. Алгассан: ${skipped.join("; ")}`, 10);
      } else {
        message.success(`${successCount}/${totalCount} харилцагчийн мэдээлэл API-аас татагдлаа`);
      }
    } catch (error) {
      console.error("Failed to fetch customer data:", error);
      message.error("Харилцагчийн мэдээлэл татахад алдаа гарлаа");
    } finally {
      setCustomerDataLoading(false);
    }
  };

  // Main process function
  const handlePart2Process = async () => {
    if (detailedWithdrawData.length === 0) {
      message.error("Дэлгэрэнгүй буцаалтын хүсэлт файл оруулна уу");
      return;
    }
    if (combinedTC.length === 0) {
      message.error("Part 1-д Trade Confirmation оруулна уу");
      return;
    }
    if (custodyInstructionData.length === 0) {
      message.error("Sheet 8-д Custody Instructions файл оруулна уу");
      return;
    }

    setPart2Loading(true);
    setLookupLoading(true);

    try {
      // Filter out excluded users
      const activeWithdrawData = detailedWithdrawData.filter(
        r => !excludedUsers.has(r.regNumber.trim())
      );
      const { bankInfoMap, balanceHistoryMap, refundHistoryMap } = await lookupUserBankInfo(activeWithdrawData);
      setUserBankInfoMap(bankInfoMap);
      setUserBalanceHistoryMap(balanceHistoryMap);
      setLookupLoading(false);

      message.success(`${Object.keys(bankInfoMap).length} хэрэглэгчийн мэдээлэл олдлоо`);

      const bondMetaMap = buildBondMetaMap(savedBondMetadata);
      const tcPriceMap = buildTCPriceMap(combinedTC);
      const portfolioAllocationsMap = await fetchPortfolioAllocations();

      const { customerMap, bondRows, maturedBondRows, stockRows } = processWithdrawData(
        activeWithdrawData,
        bankInfoMap,
        bondMetaMap,
        tcPriceMap,
        interestEndDate,
        usdRate,
        accumulatedPurchaseData,
        balanceHistoryMap,
        refundHistoryMap,
      );

      const { cashBalanceRows, missingUsdEntries } = processCashBalanceRows(
        balanceHistoryMap,
        customerMap,
        bankInfoMap,
        portfolioAllocationsMap,
        accumulatedPurchaseData,
        custodyInstructionData,
        manualSheet8UsdRates,
      );
      setMissingSheet8UsdEntries(missingUsdEntries);
      if (missingUsdEntries.length > 0) {
        message.error("Sheet 8-д custody file-ийн USD ханш дутуу байна. Зөв USD-MNT ханшийг гараар оруулна уу.");
        return;
      }

      const { balanceHistoryRows, bondRetakeRows } = processBalanceHistoryAndBondRetake(
        balanceHistoryMap, customerMap, bankInfoMap, interestEndDate, accumulatedPurchaseData
      );

      const bondRetakeRowsWithTailbar = prepareBondRetakeWithTailbar(bondRetakeRows);

      await generate8SheetExcel(
        interestEndDate,
        customerMap,
        bondRows,
        stockRows,
        balanceHistoryRows,
        accumulatedSoldStockSheets,
        cashBalanceRows,
        bondRetakeRowsWithTailbar,
        combinedTC,
        bankStatementData,
        bondRetakeRowsWithTailbar,
        bondMetaMap,
        bankInfoMap,
        accumulatedChargeSheets,
        maturedBondRows,
        accumulatedPurchaseData,
        refundHistoryMap,
        bondDistributionData,
      );

      message.success("Excel файл амжилттай татагдлаа");
    } catch (error) {
      console.error("Part 2 processing error:", error);
      if (error instanceof Error) {
        message.error(`Part 2 боловсруулахад алдаа гарлаа: ${error.message}`);
      } else {
        message.error("Part 2 боловсруулахад алдаа гарлаа");
      }
    } finally {
      setPart2Loading(false);
      setLookupLoading(false);
    }
  };

  return {
    // State values
    detailedWithdrawData,
    accumulatedPurchaseData,
    bankStatementData,
    custodyInstructionData,
    accumulatedSoldStockSheets,
    savedBondMetadata,
    accumulatedChargeSheets,
    customerBondFiles,
    interestEndDate,
    usdRate,
    userBankInfoMap,
    excludedUsers,
    missingSheet8UsdEntries,
    manualSheet8UsdRates,
    // File lists
    detailedWithdrawRequestFiles,
    purchaseHistoryFiles,
    bankStatementFiles,
    custodyInstructionFiles,
    bondMetadataFiles,
    // Loading states
    part2Loading,
    lookupLoading,
    bondMetaLoading,
    customerDataLoading,
    // Handlers
    handleDetailedWithdrawUpload,
    handlePurchaseHistoryUpload,
    handleBankStatementUpload,
    handleCustodyInstructionUpload,
    handleBondMetadataUpload,
    handleSoldStockFoundation: soldStockHandlers.handleFoundation,
    handleSoldStockAddSheet: soldStockHandlers.handleAddSheet,
    handleChargeFoundation: chargeHandlers.handleFoundation,
    handleChargeAddSheet: chargeHandlers.handleAddSheet,
    handleCustomerPurchaseFile,
    handleCustomerTxHistoryFile,
    fetchBondMetadataFromSheets,
    fetchCustomerDataFromAPI,
    handlePart2Process,
    // Setters
    setInterestEndDate,
    setUsdRate,
    setManualSheet8UsdRates,
    setAccumulatedPurchaseData,
    setPurchaseHistoryFiles,
    setCustodyInstructionData,
    setCustodyInstructionFiles,
    setAccumulatedSoldStockSheets,
    setAccumulatedChargeSheets,
    setCustomerBondFiles,
    clearSheet8UsdRequirements: () => {
      setMissingSheet8UsdEntries([]);
      setManualSheet8UsdRates({});
    },
    toggleExcludeUser: (regNumber: string) => {
      setExcludedUsers(prev => {
        const next = new Set(prev);
        if (next.has(regNumber)) {
          next.delete(regNumber);
        } else {
          next.add(regNumber);
        }
        return next;
      });
    },
    // Downloads
    downloadAccumulatedPurchaseData: () => downloadAccumulatedPurchaseData(accumulatedPurchaseData),
    downloadAccumulatedSoldStock: () => downloadAccumulatedSoldStock(accumulatedSoldStockSheets),
    downloadAccumulatedCharge: () => downloadAccumulatedCharge(accumulatedChargeSheets),
  };
};
